interface CachedAccess {
  hasAccess: boolean;
  accessLevel?: string;
  validUntil?: string;
  timestamp: number;
}

interface CacheData {
  version: number;
  entries: { [key: string]: CachedAccess };
}

interface CacheStats {
  hits: number;
  misses: number;
  size: number;
}

export class MarketAccessCache {
  private cache = new Map<string, CachedAccess>();
  private readonly TTL = 5 * 60 * 1000; // 5 minutes
  private readonly STORAGE_KEY = 'market_access_cache';
  private readonly CACHE_VERSION = 1;
  private readonly MAX_CACHE_SIZE = 1000; // Maximum entries
  private stats: CacheStats = { hits: 0, misses: 0, size: 0 };
  private storageEventHandler: ((e: any) => void) | null = null;

  constructor() {
    this.loadFromStorage();
    this.setupStorageListener();
    this.startCleanupTimer();
  }

  private getCacheKey(userId: string, marketId: number): string {
    return `${userId}:${marketId}`;
  }

  get(userId: string, marketId: number): CachedAccess | null {
    const key = this.getCacheKey(userId, marketId);
    const cached = this.cache.get(key);

    if (!cached) {
      this.stats.misses++;
      return null;
    }

    // Check if expired
    if (Date.now() - cached.timestamp > this.TTL) {
      this.cache.delete(key);
      this.saveToStorage();
      this.stats.misses++;
      return null;
    }

    // Check if access has expired based on validUntil
    if (cached.validUntil && new Date(cached.validUntil) <= new Date()) {
      this.cache.delete(key);
      this.saveToStorage();
      this.stats.misses++;
      return null;
    }

    this.stats.hits++;
    return cached;
  }

  set(
    userId: string,
    marketId: number,
    hasAccess: boolean,
    accessLevel?: string,
    validUntil?: string
  ): void {
    const key = this.getCacheKey(userId, marketId);
    
    // Implement LRU eviction if cache is too large
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const oldestKey = this.findOldestEntry();
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      hasAccess,
      accessLevel,
      validUntil,
      timestamp: Date.now(),
    });
    
    this.stats.size = this.cache.size;
    this.saveToStorage();
  }

  invalidate(userId: string): void {
    // Remove all entries for a user
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.startsWith(`${userId}:`)) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.cache.delete(key));
    this.stats.size = this.cache.size;
    this.saveToStorage();
  }

  invalidateMarket(marketId: number): void {
    // Remove all entries for a specific market
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.endsWith(`:${marketId}`)) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.cache.delete(key));
    this.stats.size = this.cache.size;
    this.saveToStorage();
  }

  clear(): void {
    this.cache.clear();
    this.stats = { hits: 0, misses: 0, size: 0 };
    this.saveToStorage();
  }

  private findOldestEntry(): string | null {
    let oldestKey: string | null = null;
    let oldestTime = Date.now();

    this.cache.forEach((value, key) => {
      if (value.timestamp < oldestTime) {
        oldestTime = value.timestamp;
        oldestKey = key;
      }
    });

    return oldestKey;
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;

      const data: CacheData = JSON.parse(stored);
      
      // Check version compatibility
      if (data.version !== this.CACHE_VERSION) {
        localStorage.removeItem(this.STORAGE_KEY);
        return;
      }

      // Restore cache, filtering out expired entries
      this.cache.clear();
      const now = Date.now();
      
      Object.entries(data.entries).forEach(([key, value]) => {
        // Skip expired entries
        if (now - value.timestamp > this.TTL) return;
        
        // Skip entries with expired validUntil
        if (value.validUntil && new Date(value.validUntil) <= new Date()) return;
        
        this.cache.set(key, value);
      });

      this.stats.size = this.cache.size;
    } catch (error) {
      console.error('Failed to load cache from storage:', error);
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  private saveToStorage(): void {
    try {
      // Only save if cache is not too large
      if (this.cache.size > this.MAX_CACHE_SIZE) {
        console.warn('Cache too large, skipping storage save');
        return;
      }

      const data: CacheData = {
        version: this.CACHE_VERSION,
        entries: Object.fromEntries(this.cache),
      };
      
      const serialized = JSON.stringify(data);
      
      // Check storage quota
      if (serialized.length > 1024 * 1024) { // 1MB limit
        console.warn('Cache data too large for storage');
        return;
      }
      
      localStorage.setItem(this.STORAGE_KEY, serialized);
    } catch (error) {
      console.error('Failed to save cache to storage:', error);
      
      // If quota exceeded, clear old data and try again
      if ((error as any)?.name === 'QuotaExceededError') {
        this.clearOldEntries();
        try {
          const data: CacheData = {
            version: this.CACHE_VERSION,
            entries: Object.fromEntries(this.cache),
          };
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
        } catch {
          // Give up if still failing
        }
      }
    }
  }

  private clearOldEntries(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.cache.forEach((value, key) => {
      if (now - value.timestamp > this.TTL / 2) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  private setupStorageListener(): void {
    // Listen for storage events to sync across tabs
    this.storageEventHandler = (e: any) => {
      if (e.key === this.STORAGE_KEY && e.newValue) {
        this.loadFromStorage();
      }
    };
    
    window.addEventListener('storage', this.storageEventHandler);
  }

  private startCleanupTimer(): void {
    // Run cleanup every minute
    window.setInterval(() => {
      this.cleanupExpired();
    }, 60 * 1000);
  }

  private cleanupExpired(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.cache.forEach((value, key) => {
      if (now - value.timestamp > this.TTL) {
        keysToDelete.push(key);
      }
    });
    
    if (keysToDelete.length > 0) {
      keysToDelete.forEach(key => this.cache.delete(key));
      this.stats.size = this.cache.size;
      this.saveToStorage();
    }
  }

  // Get cache statistics for monitoring
  getStats(): CacheStats & {
    hitRate: number;
    oldestEntry: number | null;
  } {
    let oldest: number | null = null;
    
    this.cache.forEach((entry) => {
      if (oldest === null || entry.timestamp < oldest) {
        oldest = entry.timestamp;
      }
    });

    const totalRequests = this.stats.hits + this.stats.misses;
    const hitRate = totalRequests > 0 ? this.stats.hits / totalRequests : 0;

    return {
      ...this.stats,
      hitRate,
      oldestEntry: oldest,
    };
  }

  // Cleanup method for component unmount
  destroy(): void {
    if (this.storageEventHandler) {
      window.removeEventListener('storage', this.storageEventHandler);
    }
  }
}

// Create singleton instance
export const marketAccessCache = new MarketAccessCache();

// Export for monitoring/debugging in development
if (process.env.NODE_ENV === 'development') {
  (window as any).marketAccessCache = marketAccessCache;
}